import axios, { AxiosInstance } from 'axios';
import { BillingInfo, Invoice, Subscription } from '~/types/payment';
import { downloadFileBlob } from '~/utils/download';

interface CreateSubscriptionRequest {
  currency: string;
  userId: string;
  firstName: string;
  lastName: string;
  tokenId: string;
  planCode: string;
  coupons?: string[];
  giftCard?: string;
  threeDSecureActionResultTokenId?: string;
  referralCode?: string;
}

interface UpgradeSubscriptionRequest {
  userId: string;
  subscriptionId: string;
  planCode: string;
  currency?: string;
  referralCode?: string;
}

interface UpgradeSubscriptionPreviewResponse {
  status: number;
  priceDifference: number;
}

interface UpgradeSubscriptionResponse {
  subscriptionChange: any;
}

interface UpdateBillingRequest {
  userId: string;
  billingInfoId: string;
  firstName: string;
  lastName: string;
  tokenId: string;
}

interface BillingInfoResponse {
  billingInfo: BillingInfo;
}

interface GetSubscriptionResponse {
  subscription: Subscription;
}

interface AddBillingRequest {
  userId: string;
  firstName: string;
  lastName: string;
  tokenId: string;
  primaryPaymentMethod?: boolean;
}

interface SubscriptionApi {
  create(requestData: CreateSubscriptionRequest): Promise<void>;
  upgrade(requestData: UpgradeSubscriptionRequest): Promise<UpgradeSubscriptionResponse>;
  upgradePreview(
    requestData: UpgradeSubscriptionRequest
  ): Promise<UpgradeSubscriptionPreviewResponse>;
  getBillingInfo(userId: string): Promise<BillingInfoResponse>;
  updateBillingInfo(requestData: UpdateBillingRequest): Promise<BillingInfoResponse>;
  getInvoices(userId: string): Promise<Invoice[]>;
  downloadInvoice(userId: string, invoiceId: string): Promise<void>;
  getSubscription(userId: string, subscriptionId: string): Promise<GetSubscriptionResponse>;
  redeemGiftCard(userId: string, cardNumber: string): Promise<void>;
  cancelSubscription(userId: string, subscriptionId: string): Promise<void>;
  addBillingInfo(requestData: AddBillingRequest): Promise<BillingInfoResponse>;
}

export default ($axios: AxiosInstance): SubscriptionApi => ({
  async create(requestData: CreateSubscriptionRequest): Promise<void> {
    await $axios.post('/private/v1/payment/purchase', requestData);
  },

  async upgrade({
    userId,
    subscriptionId,
    planCode,
    currency,
    referralCode,
  }: UpgradeSubscriptionRequest): Promise<UpgradeSubscriptionResponse> {
    const { data } = await $axios.post(`/private/v1/subscriptions/${subscriptionId}/change`, {
      userId,
      planCode,
      currency: currency || undefined,
      referralCode: referralCode || undefined,
    });

    return data;
  },

  async upgradePreview({
    userId,
    subscriptionId,
    planCode,
  }: UpgradeSubscriptionRequest): Promise<UpgradeSubscriptionPreviewResponse> {
    const { data } = await $axios.post(
      `/private/v1/subscriptions/${subscriptionId}/change/preview`,
      {
        userId,
        planCode,
      }
    );
    return data;
  },

  async getBillingInfo(userId: string): Promise<BillingInfoResponse> {
    const { data } = await $axios.get(`/private/v1/payment/accounts/${userId}/billing`);
    return data;
  },

  async updateBillingInfo({
    userId,
    firstName,
    lastName,
    tokenId,
    billingInfoId,
  }: UpdateBillingRequest): Promise<BillingInfoResponse> {
    const { data } = await $axios.put(`/private/v1/payment/accounts/${userId}/billing`, {
      firstName,
      lastName,
      tokenId,
      billingInfoId,
    });
    return data;
  },

  async getInvoices(userId: string): Promise<Invoice[]> {
    const { data } = await $axios.get(`/private/v1/payment/accounts/${userId}/invoices`);
    return data.invoices.map((invoice: any): Invoice => {
      const total = Number(invoice.total);
      const paidCandidates = [invoice.paid, invoice.amountPaid, invoice.paidAmount];
      const paid = paidCandidates.reduce<number | null>((foundPaidAmount, candidateValue) => {
        if (foundPaidAmount != null) {
          return foundPaidAmount;
        }

        const numericCandidateValue = Number(candidateValue);
        return Number.isFinite(numericCandidateValue) && numericCandidateValue >= 0
          ? numericCandidateValue
          : null;
      }, null);
      const invoiceState = String(invoice.state || '').toLowerCase();
      const creditAppliedCandidates = [
        invoice.creditApplied,
        invoice.creditAppliedAmount,
        invoice.credit_amount,
        invoice.creditAmount,
      ];
      const creditApplied = creditAppliedCandidates.reduce<number | null>(
        (foundCreditAmount, candidateValue) => {
          if (foundCreditAmount != null) {
            return foundCreditAmount;
          }

          const numericCandidateValue = Number(candidateValue);
          return Number.isFinite(numericCandidateValue) && numericCandidateValue > 0
            ? numericCandidateValue
            : null;
        },
        null
      );
      const hasFiniteTotal = Number.isFinite(total);
      const hasPositiveTotal = hasFiniteTotal && total > 0;
      const hasNonNegativeTotal = hasFiniteTotal && total >= 0;
      const isSettledInvoice = invoiceState === 'paid' || invoiceState === 'closed';
      const hasPaidAmount = isSettledInvoice && hasNonNegativeTotal && paid != null;
      const hasCreditApplied = creditApplied != null;
      let amount = hasFiniteTotal ? total : 0;

      if (hasPositiveTotal && hasCreditApplied) {
        amount = Math.max(total - creditApplied!, 0);
      } else if (hasPaidAmount) {
        amount = paid!;
      }

      return {
        number: invoice.number,
        id: invoice.id,
        date: invoice.closedAt,
        status: invoice.state,
        amount,
        currency: invoice.currency,
        creditApplied: hasCreditApplied ? creditApplied : null,
      };
    });
  },

  async downloadInvoice(userId: string, invoiceId: string): Promise<void> {
    try {
      const { data } = await $axios.get(
        `/private/v1/payment/accounts/${userId}/invoices/${invoiceId}/download`,
        {
          responseType: 'arraybuffer',
        }
      );
      const blob = new Blob([data], { type: 'application/pdf' });
      const filename = 'invoice.pdf';
      downloadFileBlob(blob, filename);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const data = error.response?.data;
        if (data) {
          // Data will be an instance of ArrayBuffer
          const decoder = new TextDecoder('utf-8');
          const decodedError = decoder.decode(data);

          throw new Error(`There was an error downloading your file: ${decodedError || data}`);
        }
      }

      throw error;
    }
  },

  async getSubscription(userId: string, subscriptionId: string): Promise<GetSubscriptionResponse> {
    const { data } = await $axios.get(
      `/private/v1/payment/accounts/${userId}/subscriptions/${subscriptionId}`
    );
    return data;
  },

  async redeemGiftCard(userId: string, cardNumber: string): Promise<void> {
    await $axios.post(`/private/v1/payment/gift-cards/${cardNumber}/redeem`, {
      userId,
    });
  },

  async cancelSubscription(userId: string, subscriptionId: string): Promise<void> {
    await $axios.put(
      `/private/v1/payment/accounts/${userId}/subscriptions/${subscriptionId}/cancel`
    );
  },

  async addBillingInfo({
    userId,
    firstName,
    lastName,
    tokenId,
    primaryPaymentMethod,
  }: AddBillingRequest): Promise<BillingInfoResponse> {
    const { data } = await $axios.post(`/private/v1/payment/accounts/${userId}/billing/add`, {
      firstName,
      lastName,
      tokenId,
      primaryPaymentMethod,
    });
    return data;
  },
});
